import { isDefined } from '../../utils/variables';
import { SkyMapAxiosServiceFactory } from '../axios/skymap-axios-service-factory';
import { getZitadelOidcClient } from './zitadel-user-manager';

/**
 *  Completing an invitation inside this environment's own authorization request.
 *
 *  **The problem.** The set-password link the backend hands back opens Zitadel's verify page on its
 *  own, outside any authorization request. When the person has chosen a password or a passkey, the
 *  login has nowhere to send them but the organisation-wide default redirect URI, which is one URL
 *  for the whole estate and points at the production portal. Somebody accepting an invitation on
 *  development or staging therefore lands on production, signed out.
 *
 *  **The fix.** Zitadel's verify page accepts a `requestId`. Given one, it completes the invitation
 *  inside that request and finishes it the ordinary way, which returns the person to the
 *  `redirect_uri` of whichever environment started it — already signed in.
 *
 *  Nothing here fails loudly. An invitation that cannot be paired with a request still works
 *  exactly as it does today, so every failure ends in "open the link as it came".
 */

/** The query parameter Zitadel's verify page reads the authorization request's name from. */
const REQUEST_ID_PARAMETER = 'requestId';

/**
 *  Puts `requestId` on an invitation URL, and returns the URL untouched when there is none.
 *
 *  Kept separate from the asking, and pure, because this is the half worth being exact about: the
 *  URL carries a single-use code in its existing query, and rebuilding it rather than appending to
 *  it is what guarantees that code survives unchanged.
 *
 *  A URL that cannot be parsed is returned as it came. It is not this function's business to decide
 *  that the backend sent something unusable — the navigation that follows will say so far more
 *  clearly than a thrown error here would.
 */
export const buildInvitationUrl = (invitationUrl: string, requestId?: string): string => {
  if (!isDefined(requestId) || requestId.length === 0) {
    return invitationUrl;
  }

  let url: URL;

  try {
    url = new URL(invitationUrl);
  } catch {
    return invitationUrl;
  }

  url.searchParams.set(REQUEST_ID_PARAMETER, requestId);

  return url.toString();
};

/**
 *  Starts an authorization request for this environment and returns the name Zitadel gave it.
 *
 *  Two steps, and both matter. `createSigninRequest` builds the request *and stores its state and
 *  PKCE verifier*, exactly as pressing "sign in" would, which is what lets the callback route
 *  redeem the code afterwards. The backend then reads the name out of Zitadel's redirect, which a
 *  browser cannot do: that redirect is only readable server-side (SK-7425).
 *
 *  `undefined` whenever the name cannot be had — Zitadel unconfigured here, the request unbuildable,
 *  the backend or Zitadel unavailable. The caller opens the invitation without one.
 *
 *  Deliberately quiet about detail on failure: the reason is worth a line in the console, but the
 *  request it was for is about to be combined with a single-use invitation code, so no part of it
 *  is logged.
 */
export const startInvitationAuthorizationRequest = async (): Promise<string | undefined> => {
  try {
    // `si:r` is what `signinRedirect` records, and the stored state is read back by the same
    // `signinRedirectCallback`, so the request has to be labelled the way that path expects.
    const request = await getZitadelOidcClient().createSigninRequest({ request_type: 'si:r' });

    // The internal API rather than the public one: this is portal-only plumbing, and that gateway
    // answers only the handful of origins the portal is served from. It is reachable while signed
    // out, which is the state everybody completing an invitation is in.
    const authService = SkyMapAxiosServiceFactory.instance.createAuthServiceInternal();
    const { data } = await authService.startZitadelAuthorizationRequest({
      body: { query: new URL(request.url).searchParams.toString() },
    });

    return data.requestId;
  } catch {
    // Swallowed on purpose. An invitation that could not be paired with a request is still a
    // working invitation, and telling the person about a failure they cannot act on would be worse
    // than sending them the long way round.
    console.warn('Could not start an authorization request for this invitation.');
    return undefined;
  }
};
